import 'react-json-view-lite/dist/index.css'

import ErrorBoundary from '@components/shared/error-boundary'
import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  JsonView,
  collapseAllNested,
  defaultStyles,
} from 'react-json-view-lite'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { UserType } from '@types'
import { getRewards } from '@store/actions/users'
import { makeStyles } from 'tss-react/mui'

dayjs.extend(utc)
dayjs.extend(timezone)

interface Expiration {
  expirationDate: string
}

interface Balance {
  balance: string
  expirations?: Expiration[]
  name: string
  walletCode: number
}

interface RewardsResponse {
  pointBalances?: Balance[]
  rewardBalances?: Balance[]
}

const getNextExpiration = (expirations: Expiration[] = []) => {
  const now = dayjs()

  const [next] = expirations
    .map(({ expirationDate }) =>
      dayjs.tz(expirationDate, 'US/Pacific').endOf('day'),
    )
    .filter((expiration) => expiration.isAfter(now))
    .sort((a, b) => a.valueOf() - b.valueOf())

  return next ?? null
}

const formatExpiration = (expirations?: Expiration[]) =>
  getNextExpiration(expirations)?.format('MMM D, YYYY') ?? '—'

const columns: { title: string; render: (row: Balance) => ReactNode }[] = [
  { title: 'Name', render: (row) => row.name },
  { title: 'Balance', render: (row) => row.balance },
  { title: 'Wallet Code', render: (row) => row.walletCode },
  { title: 'Expires', render: (row) => formatExpiration(row.expirations) },
]

const sections: { key: keyof RewardsResponse; title: string }[] = [
  { key: 'pointBalances', title: 'Point Balances' },
  { key: 'rewardBalances', title: 'Reward Balances' },
]

interface Props {
  user: UserType
}

const Rewards = ({ user }: Props): ReactElement => {
  const { classes } = useStyles()
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [rewardsJson, setRewardsJson] = useState<RewardsResponse | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const rewards = await getRewards(user.id)
        setRewardsJson(rewards)
      } catch (e: any) {
        setErrorMessage(e.message)
      }
    })()
  }, [user])

  return (
    <>
      <h3>Rewards Details</h3>
      {errorMessage && (
        <p className={classes.error}>
          Cannot load rewards info: {errorMessage}
        </p>
      )}
      {rewardsJson && (
        <>
          <ErrorBoundary>
            <TableContainer className={classes.table} component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map(({ title }) => (
                      <TableCell key={title}>{title}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sections.map(({ key, title }) => {
                    const balances = rewardsJson[key] ?? []

                    return (
                      <Fragment key={key}>
                        <TableRow>
                          <TableCell
                            className={classes.sectionHeader}
                            colSpan={columns.length}
                          >
                            {title}
                          </TableCell>
                        </TableRow>
                        {balances.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={columns.length}>None</TableCell>
                          </TableRow>
                        )}
                        {balances.map((balance) => (
                          <TableRow key={`${key}_${balance.walletCode}`}>
                            {columns.map(({ render, title: columnTitle }) => (
                              <TableCell key={columnTitle}>
                                {render(balance)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </ErrorBoundary>
          <h4>Raw Paytronix Data</h4>
          <div className={classes.viewer}>
            <JsonView
              data={rewardsJson}
              shouldExpandNode={collapseAllNested}
              style={defaultStyles}
            />
          </div>
        </>
      )}
    </>
  )
}

export default Rewards

const useStyles = makeStyles()((theme) => ({
  error: {
    color: 'red',
  },
  table: {
    marginBottom: theme.spacing(2),
  },
  sectionHeader: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: 600,
  },
  viewer: {
    overflow: 'auto',
  },
}))
